import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { ComprehensiveStockAnalysis } from '@/types/perplexity.types';
import { SkeletonCard } from './SkeletonCard';

interface InvestmentOutlookCardProps {
  data: ComprehensiveStockAnalysis['investmentOutlook'] | null | undefined;
  isLoading: boolean;
}

export const InvestmentOutlookCard: React.FC<InvestmentOutlookCardProps> = ({
  data,
  isLoading,
}) => {
  if (isLoading || !data) {
    return <SkeletonCard lines={4} />;
  }

  const getStanceColor = (stance: 'bullish' | 'neutral' | 'bearish') => {
    switch (stance) {
      case 'bullish':
        return '#34C759';
      case 'bearish':
        return '#FF3B30';
      case 'neutral':
        return Colors.text.secondary;
    }
  };

  const getStanceIcon = (stance: 'bullish' | 'neutral' | 'bearish') => {
    switch (stance) {
      case 'bullish':
        return 'arrow-up-circle';
      case 'bearish':
        return 'arrow-down-circle';
      case 'neutral':
        return 'remove-circle';
    }
  };

  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <Ionicons name="telescope" size={20} color={Colors.primary} />
        <Text style={styles.cardTitle}>Investment Outlook</Text>
        <View
          style={[
            styles.stanceBadge,
            { backgroundColor: getStanceColor(data.overallStance) + '20' },
          ]}
        >
          <Ionicons
            name={getStanceIcon(data.overallStance)}
            size={14}
            color={getStanceColor(data.overallStance)}
          />
          <Text style={[styles.stanceText, { color: getStanceColor(data.overallStance) }]}>
            {data.overallStance.toUpperCase()}
          </Text>
        </View>
      </View>

      {/* Short-term Outlook */}
      <View style={styles.outlookSection}>
        <View style={styles.outlookHeader}>
          <Ionicons name="calendar" size={16} color={Colors.primary} />
          <Text style={styles.outlookLabel}>Short-term (3-6 months)</Text>
        </View>
        <Text style={styles.outlookText}>{data.shortTerm}</Text>
      </View>

      <View style={styles.divider} />

      {/* Long-term Outlook */}
      <View style={styles.outlookSection}>
        <View style={styles.outlookHeader}>
          <Ionicons name="calendar-outline" size={16} color={Colors.primary} />
          <Text style={styles.outlookLabel}>Long-term (1-3 years)</Text>
        </View>
        <Text style={styles.outlookText}>{data.longTerm}</Text>
      </View>

      <View style={styles.divider} />

      {/* Confidence Level */}
      <View style={styles.confidenceSection}>
        <View style={styles.confidenceHeader}>
          <Ionicons name="checkmark-circle" size={16} color={Colors.primary} />
          <Text style={styles.confidenceLabel}>Confidence</Text>
        </View>
        <Text style={styles.confidenceText}>{data.confidence}</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
    flex: 1,
  },
  stanceBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    gap: 4,
  },
  stanceText: {
    fontSize: 12,
    fontWeight: '700',
  },
  outlookSection: {
    marginBottom: 8,
  },
  outlookHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
  },
  outlookLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: Colors.text.secondary,
  },
  outlookText: {
    fontSize: 14,
    lineHeight: 20,
    color: Colors.text.primary,
    marginLeft: 22,
  },
  divider: {
    height: 1,
    backgroundColor: Colors.border.subtle,
    marginVertical: 12,
  },
  confidenceSection: {
    marginTop: 4,
  },
  confidenceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
  },
  confidenceLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: Colors.text.secondary,
  },
  confidenceText: {
    fontSize: 14,
    lineHeight: 20,
    color: Colors.text.primary,
    marginLeft: 22,
  },
});
